import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import Button from '@material-ui/core/Button'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Typography from '@material-ui/core/Typography'
import Dialog from 'components/dialog'
import { useDialogTitleStyles } from 'components/dialog/use-dialog-title-styles'
import FileDownloadIcon from 'components/material-symbol/icons/FileDownload'
import GraduateCard from './graduate-card'
import { saveCardImage } from './save-card-image'

const TITLE_ID = 'pga-hope-graduate-card-title'

const useStyles = makeStyles(theme => ({
  paper: {
    [theme.breakpoints.down('sm')]: {
      borderRadius: 0,
    },
  },
  content: {
    // DialogContent scrolls, so it clips anything outside its padding box:
    // keep room above and around the card for its drop shadow.
    paddingTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(3),
    // The card sizes its type from the viewport minus 1rem per side, so on a
    // phone the content gutter must match that.
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 2, 2),
    },
  },
}))

const Header = () => {
  const dialogClasses = useDialogTitleStyles()
  return (
    <DialogTitle disableTypography classes={{ root: dialogClasses.titleRoot }}>
      <Typography component="h2" id={TITLE_ID} className={dialogClasses.title}>
        PGA HOPE Graduate Card
      </Typography>
      <Typography className={dialogClasses.bodyText}>
        Show your digital card to redeem partner discounts, or download it to
        keep on your phone.
      </Typography>
    </DialogTitle>
  )
}

const GraduateCardBody = ({
  card,
  closeDialog,
  verifiedOn,
  verificationFailed,
}) => {
  const classes = useStyles()
  const dialogClasses = useDialogTitleStyles()
  const cardRef = useRef(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(false)

  // Only the card element is captured, so dialog chrome never ends up in the PNG.
  const download = async () => {
    setSaving(true)
    setError(false)
    try {
      await saveCardImage(cardRef.current, card)
    } catch (e) {
      setError(true)
      window.rg4js && window.rg4js('send', { error: e })
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Header />
      <DialogContent className={clsx(dialogClasses.content, classes.content)}>
        <GraduateCard
          ref={cardRef}
          name={card.name}
          serviceBranch={card.serviceBranch}
          sectionName={card.sectionName}
          graduationYear={card.graduationYear}
          verifiedOn={verifiedOn}
          verificationFailed={verificationFailed}
        />
        {error ? (
          <Typography role="alert" variant="body2" color="error">
            We couldn&apos;t save your card. Please try again.
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions className={dialogClasses.actions}>
        <Button color="primary" size="large" onClick={closeDialog}>
          Close
        </Button>
        <Button
          color="primary"
          variant="contained"
          size="large"
          disableElevation
          startIcon={<FileDownloadIcon />}
          onClick={download}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Download card'}
        </Button>
      </DialogActions>
    </>
  )
}

GraduateCardBody.propTypes = {
  card: PropTypes.object.isRequired,
  closeDialog: PropTypes.func.isRequired,
  verifiedOn: PropTypes.instanceOf(Date),
  verificationFailed: PropTypes.bool,
}

// Always one card: when a golfer has several, the caller passes the latest.
const GraduateCardModal = ({
  open,
  onClose,
  card,
  verifiedOn,
  verificationFailed,
}) => {
  const classes = useStyles()
  const dialogClasses = useDialogTitleStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Dialog
      isOpen={open}
      onClose={onClose}
      showCloseButton
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      aria-labelledby={TITLE_ID}
      classes={{ paper: clsx(dialogClasses.dialogPaper, classes.paper) }}
    >
      {({ closeDialog }) => (
        <GraduateCardBody
          card={card}
          closeDialog={closeDialog}
          verifiedOn={verifiedOn}
          verificationFailed={verificationFailed}
        />
      )}
    </Dialog>
  )
}

GraduateCardModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  card: PropTypes.object.isRequired,
  verifiedOn: PropTypes.instanceOf(Date),
  verificationFailed: PropTypes.bool,
}

export default GraduateCardModal
